import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Radio, Clock, Trophy, Target, AlertTriangle, Timer } from "lucide-react";

const MatchDayLive = () => {
  const [data, setData] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [pulseGoal, setPulseGoal] = useState(false);
  const prevEventsLen = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/live-match/men`);
        const d = res.data;
        if (d.status === "no_match") {
          setData(null);
          // No match — poll infrequently (2 minutes)
          resetInterval(120000);
          return;
        }
        // Detect new goal event
        if (d.events && d.events.length > prevEventsLen.current) {
          setPulseGoal(true);
          setTimeout(() => setPulseGoal(false), 3000);
        }
        prevEventsLen.current = d.events?.length || 0;
        setData(d);
        // Live or pre-match — poll every 30s
        resetInterval(30000);
      } catch {
        // silent fail — poll infrequently on error
        resetInterval(120000);
      }
    };

    const resetInterval = (ms) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchLive, ms);
    };

    fetchLive();
    // Start with infrequent polling; fetchLive will speed it up if a match is found
    intervalRef.current = setInterval(fetchLive, 120000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Countdown timer for pre-match
  useEffect(() => {
    if (!data || data.status !== "pre_match" || !data.match?.countdown_to) return;
    const tick = () => {
      const diff = new Date(data.match.countdown_to) - new Date();
      if (diff <= 0) { setCountdown(null); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [data]);

  if (!data) return null;

  const { status, match, events } = data;
  const isLive = status === "live" || status === "half_time";
  const isPreMatch = status === "pre_match";
  const isFullTime = status === "full_time";

  // Determine West Ham's perspective
  const whIsHome = match.is_home;
  const whScore = whIsHome ? match.home_score : match.away_score;
  const oppScore = whIsHome ? match.away_score : match.home_score;
  const oppName = whIsHome ? match.away_team : match.home_team;

  // Result class for full-time
  const getResultClass = () => {
    if (!isFullTime) return "";
    if (whScore > oppScore) return "from-green-700 to-green-900";
    if (whScore < oppScore) return "from-red-800 to-red-950";
    return "from-slate-700 to-slate-900";
  };

  return (
    <div
      className={`relative overflow-hidden transition-all duration-500 ${
        isLive ? "bg-gradient-to-r from-[#0A0F1A] to-[#111827]" :
        isPreMatch ? "bg-gradient-to-r from-[#1a1a2e] to-[#16213e]" :
        `bg-gradient-to-r ${getResultClass()}`
      }`}
      data-testid="match-day-live-widget"
    >
      {/* Animated background for live matches */}
      {isLive && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-[0.03]"
            style={{
              backgroundImage: "repeating-conic-gradient(#2BAEE5 0% 25%, transparent 0% 50%)",
              backgroundSize: "40px 40px",
              animation: "spin 60s linear infinite"
            }}
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isLive && (
              <span className="flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" data-testid="live-badge">
                <Radio className="w-3 h-3 animate-pulse" />
                LIVE
              </span>
            )}
            {isPreMatch && (
              <span className="flex items-center gap-1.5 bg-[#2BAEE5] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" data-testid="prematch-badge">
                <Timer className="w-3 h-3" />
                UPCOMING
              </span>
            )}
            {isFullTime && (
              <span className="flex items-center gap-1.5 bg-white/20 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" data-testid="fulltime-badge">
                <Trophy className="w-3 h-3" />
                FULL TIME
              </span>
            )}
            {status === "half_time" && (
              <span className="flex items-center gap-1.5 bg-amber-500 text-black text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ml-2">
                HT
              </span>
            )}
            <span className="text-white/50 text-xs">{match.competition}</span>
          </div>
          <Link to="/fixtures" className="text-white/40 hover:text-white/70 text-xs transition-colors">
            All Fixtures →
          </Link>
        </div>

        {/* Main Scoreboard */}
        <div className="flex items-center justify-center gap-4 sm:gap-8">
          {/* Home Team */}
          <div className="flex-1 text-right">
            <div className={`text-lg sm:text-2xl font-bold text-white tracking-wide ${whIsHome ? '' : 'opacity-70'}`}
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="home-team-name"
            >
              {match.home_team?.toUpperCase()}
            </div>
            {whIsHome && <div className="text-[10px] text-[#2BAEE5] font-semibold tracking-wider mt-0.5">HAMMERS</div>}
          </div>

          {/* Score / Countdown */}
          <div className="flex-shrink-0">
            {isPreMatch && countdown ? (
              <div className="text-center" data-testid="countdown">
                <div className="flex items-center gap-1 text-white">
                  {[
                    { val: countdown.h, label: "H" },
                    { val: countdown.m, label: "M" },
                    { val: countdown.s, label: "S" },
                  ].map((unit, i) => (
                    <div key={i} className="flex items-center">
                      {i > 0 && <span className="text-white/30 text-lg mx-0.5">:</span>}
                      <div className="bg-white/10 rounded px-2 py-1 min-w-[36px] text-center backdrop-blur-sm">
                        <div className="text-xl sm:text-2xl font-bold tabular-nums" style={{ fontFamily: "Barlow Condensed" }}>
                          {String(unit.val).padStart(2, "0")}
                        </div>
                        <div className="text-[8px] text-white/40 -mt-0.5">{unit.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-white/40 mt-1">KICK OFF</div>
              </div>
            ) : (
              <div className={`flex items-center gap-3 ${pulseGoal ? "animate-bounce" : ""}`} data-testid="scoreboard">
                <div className={`text-4xl sm:text-5xl font-bold text-white tabular-nums ${pulseGoal ? "text-[#F8D742]" : ""}`}
                  style={{ fontFamily: "Barlow Condensed", transition: "color 0.5s" }}
                  data-testid="home-score"
                >
                  {match.home_score}
                </div>
                <div className="flex flex-col items-center">
                  {isLive && (
                    <div className="text-[#F8D742] text-xs font-bold animate-pulse" data-testid="match-minute">
                      {match.minute}'
                    </div>
                  )}
                  <div className="w-4 h-px bg-white/30 my-0.5"></div>
                </div>
                <div className={`text-4xl sm:text-5xl font-bold text-white tabular-nums ${pulseGoal ? "text-[#F8D742]" : ""}`}
                  style={{ fontFamily: "Barlow Condensed", transition: "color 0.5s" }}
                  data-testid="away-score"
                >
                  {match.away_score}
                </div>
              </div>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1">
            <div className={`text-lg sm:text-2xl font-bold text-white tracking-wide ${!whIsHome ? '' : 'opacity-70'}`}
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="away-team-name"
            >
              {match.away_team?.toUpperCase()}
            </div>
            {!whIsHome && <div className="text-[10px] text-[#2BAEE5] font-semibold tracking-wider mt-0.5">HAMMERS</div>}
          </div>
        </div>

        {/* Events Timeline */}
        {events && events.length > 0 && (
          <div className="mt-3 flex items-center justify-center gap-3 flex-wrap" data-testid="events-timeline">
            {events.map((ev, i) => (
              <div key={i}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
                  ev.type === "goal" ? "bg-[#F8D742]/20 text-[#F8D742]" :
                  ev.type === "red_card" ? "bg-red-500/20 text-red-400" :
                  "bg-white/10 text-white/60"
                }`}
              >
                {ev.type === "goal" && <Target className="w-3 h-3" />}
                {ev.type === "red_card" && <AlertTriangle className="w-3 h-3" />}
                <span className="font-medium">{ev.minute}'</span>
                <span>{ev.description}</span>
              </div>
            ))}
          </div>
        )}

        {/* Full-time result label */}
        {isFullTime && match.result_label && (
          <div className="mt-2 text-center">
            <span className={`text-sm font-bold uppercase tracking-widest ${
              match.result_label === "WIN" ? "text-green-400" :
              match.result_label === "DEFEAT" ? "text-red-400" :
              "text-white/60"
            }`}
              data-testid="result-label"
            >
              {match.result_label}
            </span>
          </div>
        )}
      </div>

      {/* CSS for spin animation */}
      {isLive && (
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      )}
    </div>
  );
};

export default MatchDayLive;
